import React, { useState, useEffect } from 'react';
import { marked } from 'marked';

import { motion } from 'framer-motion';
import { X, Check, Loader } from 'lucide-react';
import RichEditor from './RichEditor';
import { API } from '../App';

export default function BoxEditor({ box, depth, color, onSave, onClose, error }) {
  const [title,   setTitle]   = useState(box?.title   || '');
  const [saving,  setSaving]  = useState(false);

  const contentType = box?.content_type || 'text';

  useEffect(() => {
    function onKey(e) { if (e.key === 'Escape') onClose(); }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  // Pre-convert content to HTML so BoxEditor state starts as HTML
  // (matches what Tiptap's onUpdate returns, so save works even without typing)
  const initialContent = React.useMemo(() => {
    const raw = box?.content || '';
    if (!raw) return '';
    if (contentType === 'document') return '';
    if (contentType === 'image') {
      const src = raw.startsWith('http') ? raw : `${API}${raw.startsWith('/') ? '' : '/'}${raw}`;
      return `<img src="${src}" alt="${box?.title || ''}" />`;
    }
    // Already HTML? pass through. Otherwise parse markdown → HTML.
    if (raw.trimStart().startsWith('<')) return raw;
    return marked.parse(raw);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const [content, setContent] = useState(initialContent);
  const canSave = title.trim() || (contentType !== 'document' && content.trim());

  async function handleSave() {
    setSaving(true);
    if (contentType === 'document') {
      await onSave({ title: title.trim(), content_type: 'document', content: box.content });
      setSaving(false);
      return;
    }
    // Rewrite relative /uploads/ paths to absolute so images render everywhere
    const fixedContent = content.replace(
      /src="(\/uploads\/[^"]+)"/g,
      (_, path) => `src="${API}${path}"`
    );
    // Image cards store a single <img> tag as their whole content. If the user removed
    // that image in the editor, the content no longer matches an image card — convert it
    // to a normal text card instead of staying stuck as content_type='image' forever
    // (which would otherwise try to render arbitrary text/HTML as an <img src="...">).
    const stillHasImage = /<img[^>]*\ssrc=/i.test(fixedContent);
    const saveType = (contentType === 'image' && stillHasImage) ? 'image' : 'text';
    await onSave({ title, content_type: saveType, content: fixedContent });
    setSaving(false);
  }

  return (
    <motion.div
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      style={{
        position: 'fixed', inset: 0, zIndex: 400,
        background: 'rgba(0,0,0,0.55)', backdropFilter: 'blur(6px)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24,
      }}
      onClick={onClose}
    >
      <motion.div
        initial={{ opacity: 0, scale: 0.93, y: 16 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.93 }}
        onClick={e => e.stopPropagation()}
        style={{
          width: 720, maxWidth: '96vw', height: '80vh',
          display: 'flex', flexDirection: 'column',
          background: 'var(--bg-3)', border: `1px solid ${color}40`,
          borderRadius: 18,
          boxShadow: `0 0 40px ${color}20, 0 24px 64px rgba(0,0,0,0.5)`,
          overflow: 'hidden',
        }}
      >
        {/* Header */}
        <div style={{
          padding: '14px 18px', borderBottom: '1px solid var(--border)',
          display: 'flex', alignItems: 'center', gap: 10, flexShrink: 0,
        }}>
          <div style={{ width: 10, height: 10, borderRadius: '50%', background: color, flexShrink: 0 }} />
          <h2 style={{ fontSize: 14, fontWeight: 700, color: 'var(--text-primary)', flex: 1, margin: 0 }}>
            {box ? 'Edit Card' : 'New Card'}
          </h2>
          <motion.button whileHover={{ scale: 1.1 }} whileTap={{ scale: 0.9 }} onClick={onClose}
            style={{
              background: 'var(--bg-4)', border: '1px solid var(--border)',
              borderRadius: 8, padding: 6, cursor: 'pointer', color: 'var(--text-muted)', display: 'flex',
            }}>
            <X size={14} />
          </motion.button>
        </div>

        {/* Title */}
        <div style={{ padding: '14px 20px 0', flexShrink: 0 }}>
          <input
            autoFocus
            value={title}
            onChange={e => setTitle(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') e.preventDefault(); }}
            placeholder="Card title…"
            style={{
              width: '100%', background: 'transparent', border: 'none',
              borderBottom: `2px solid ${color}30`, borderRadius: 0,
              padding: '4px 0 8px', color: 'var(--text-primary)',
              fontSize: 20, fontWeight: 700, outline: 'none',
              fontFamily: 'Inter, -apple-system, sans-serif',
            }}
            onFocus={e => e.target.style.borderBottomColor = color}
            onBlur={e => e.target.style.borderBottomColor = `${color}30`}
          />
        </div>

        {/* Rich editor */}
        <div style={{ flex: 1, minHeight: 0, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
          {contentType === 'document' ? <div style={{ padding: 20, color: 'var(--text-muted)', fontSize: 13 }}>Attached file: {box.content}. Rename this card here; use the card’s Open or Download button for the file.</div> : <RichEditor
            content={content}
            onChange={setContent}
            color={color}
            placeholder="Write anything — use the toolbar for formatting, images, tables, and links…"
          />}
        </div>

        {error && <div role="alert" style={{ padding: '9px 18px', color: '#dc2626', fontSize: 12 }}>{error}</div>}
        {/* Footer */}
        <div style={{
          padding: '12px 18px', borderTop: '1px solid var(--border)',
          display: 'flex', gap: 10, alignItems: 'center', flexShrink: 0,
        }}>
          <span style={{ flex: 1, fontSize: 11, color: 'var(--text-faint)' }}>
            {contentType === 'document' ? 'Renaming file card' : `${box ? 'Editing card' : 'New card'} · Images can be dragged into the editor`}
          </span>
          <button onClick={onClose} style={cancelBtn}>Cancel</button>
          <motion.button
            whileHover={{ scale: canSave ? 1.02 : 1 }}
            whileTap={{ scale: canSave ? 0.98 : 1 }}
            onClick={handleSave}
            disabled={!canSave || saving}
            style={{
              padding: '9px 22px', borderRadius: 9, border: 'none',
              background: canSave ? color : 'var(--bg-4)',
              color: canSave ? '#fff' : 'var(--text-muted)',
              cursor: canSave ? 'pointer' : 'not-allowed',
              fontSize: 14, fontWeight: 600, fontFamily: 'inherit',
              display: 'flex', alignItems: 'center', gap: 8,
              boxShadow: canSave ? `0 0 16px ${color}40` : 'none',
              transition: 'all 0.2s',
            }}>
            {saving
              ? <motion.div animate={{ rotate: 360 }} transition={{ repeat: Infinity, duration: 0.8, ease: 'linear' }}><Loader size={14} /></motion.div>
              : <Check size={14} />}
            {box ? 'Save' : 'Add Card'}
          </motion.button>
        </div>
      </motion.div>
    </motion.div>
  );
}

const cancelBtn = {
  padding: '9px 16px', borderRadius: 9, background: 'var(--bg-4)',
  border: '1px solid var(--border)', color: 'var(--text-muted)',
  cursor: 'pointer', fontSize: 14, fontWeight: 500, fontFamily: 'inherit',
};
